import React from 'react';
import { Space, Tag } from 'antd';
import {
  ThunderboltOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { ProjectActiveTag } from '../pages/Projects/ProjectsStyle';

/**
 * StatusTag Component
 *
 * A reusable component for displaying status tags with consistent styling and icons.
 *
 * @param {string} status - The status to display
 * @param {boolean} showIcon - Whether to show the status icon (default: true)
 * @param {string} className - Additional CSS classes to apply
 *
 * Supported Status Types:
 * Campaign Statuses:
 * - "Saved": Blue background with active icon
 * - "Ready": Blue background with active icon
 * - "Published": Green background with completed icon
 * - "In-Progress": Blue background with active icon
 * - "Paused": Orange background with paused icon
 * - "Completed": Green background with completed icon
 * - "Closed": Green background with completed icon
 *
 * General Statuses:
 * - "Active": Green background with active icon
 * - "Inactive": Orange background with paused icon
 * - "On Hold": Orange background with on-hold icon
 *
 * Usage Examples:
 * <StatusTag status="In-Progress" />
 * <StatusTag status="Completed" showIcon={false} />
 * <StatusTag status="Paused" className="custom-class" />
 */

// Helper function to get status tag configuration
const getStatusConfig = (status) => {
  const statusConfigs = {
    // Campaign statuses
    "Saved": {
      className: "bgSkyBlueBorder",
      icon: "/images/active-tag-icon.svg",
      color: "blue"
    },
    "Ready": {
      className: "bgSkyBlueBorder",
      icon: "/images/active-tag-icon.svg",
      color: "blue"
    },
    "In Review": {
      className: "bgGreenBorder",
      icon: "/images/completed-icon.svg",
      color: "green"
    },
    "In-Progress": {
      className: "bgSkyBlueBorder",
      icon: "/images/active-tag-icon.svg",
      color: "blue"
    },
    "Paused": {
      className: "bgOrangeBorder",
      icon: "/images/paused-icon.svg",
      color: "orange"
    },
    "Completed": {
      className: "bgGreenBorder",
      icon: "/images/completed-icon.svg",
      color: "green"
    },
    "Closed": {
      className: "bgGreenBorder",
      icon: "/images/completed-icon.svg",
      color: "green"
    },
    // Other statuses (for UserManagement, OrganizationManagement, SurveysList, etc.)
    "Active": {
      className: "bgGreenBorder",
      icon: "/images/active-tag-icon.svg",
      color: "green"
    },
    "Inactive": {
      className: "bgOrangeBorder",
      icon: "/images/paused-icon.svg",
      color: "orange"
    },
    "On Hold": {
      className: "bgOrangeBorder",
      icon: "/images/on-hold-tag-icon.svg",
      color: "orange"
    }
  };

  return statusConfigs[status] || statusConfigs["Saved"];
};

const StatusTag = ({ status, showIcon = true, className = "" }) => {
  const config = getStatusConfig(status);

  return (
    <ProjectActiveTag className={`${config.className} ${className}`}>
      <Space size={6}>
        {showIcon && <img src={config.icon} alt={`${status} status`} />}
        {status}
      </Space>
    </ProjectActiveTag>
  );
};

// ============================================
// STUDY LISTING PAGE STATUS TAG
// Uses same colors and icons as Study Detail page
// ============================================

// Get Ant Design icon based on icon type
const getStudyStatusIcon = (iconType, color) => {
  const iconStyle = { color, fontSize: '12px' };
  switch (iconType) {
    case 'thunder':
      return <ThunderboltOutlined style={iconStyle} />;
    case 'play':
      return <PlayCircleOutlined style={iconStyle} />;
    case 'pause':
      return <PauseCircleOutlined style={iconStyle} />;
    case 'check':
      return <CheckCircleOutlined style={iconStyle} />;
    case 'cloud':
      return <CloudUploadOutlined style={iconStyle} />;
    default:
      return <ThunderboltOutlined style={iconStyle} />;
  }
};

// Configuration for Study listing page - matches Study Detail page colors
// Background uses color + 1A (10% opacity) to match other listing page tag styling
const getStudyListingStatusConfig = (status) => {
  const statusConfigs = {
    "Saved": { color: '#4B5563', background: '#4B55631A', iconType: 'thunder' },
    "Ready": { color: '#047857', background: '#0478571A', iconType: 'thunder' },
    "In Review": { color: '#6B4EAF', background: '#6B4EAF1A', iconType: 'cloud' },
    "Live": { color: '#1B8844', background: '#1B88441A', iconType: 'play' },
    "In-Progress": { color: '#1B8844', background: '#1B88441A', iconType: 'play' },
    "Paused": { color: '#B45309', background: '#B453091A', iconType: 'pause' },
    "Completed": { color: '#15803D', background: '#15803D1A', iconType: 'check' },
    "Closed": { color: '#991B1B', background: '#991B1B1A', iconType: 'check' },
  };
  return statusConfigs[status] || statusConfigs["Saved"];
};

// Map backend status to display text for Study listing page
export const mapStudyStatusToDisplay = (status) => {
  const statusMap = {
    'saved': 'Saved',
    'ready': 'Ready',
    'published': 'In Review',
    'in_progress': 'Live',
    'paused': 'Paused',
    'completed': 'Completed',
    'closed': 'Closed',
  };
  return statusMap[status] || status;
};

// Study Status Tag - only for Study/Campaign listing page
// Uses same styling as other listing page tags (padding, font-size, border-radius)
export const StudyStatusTag = ({ status, showIcon = true, className = "" }) => {
  const config = getStudyListingStatusConfig(status);

  return (
    <ProjectActiveTag
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        fontSize: '12px',
        borderRadius: '5px',
        lineHeight: 1,
        backgroundColor: config.background,
        color: config.color,
      }}
    >
      <Space size={4}>
        {showIcon && getStudyStatusIcon(config.iconType, config.color)}
        {status}
      </Space>
    </ProjectActiveTag>
  );
};

// ============================================
// STUDY DETAIL PAGE STATUS CONFIG
// Used for: CampaignDetail.js (Study detail page)
// Returns full config including card styling colors
// ============================================

// Get full status config for Study Detail page
export const getStudyDetailStatusConfig = (status) => {
  const statusConfigs = {
    'saved': {
      text: 'Saved',
      color: '#4B5563',
      iconType: 'thunder',
      cardBackground: '#F5F5F7',
      cardBorder: '#E5E5E5',
    },
    'published': {
      text: 'In Review',
      color: '#6B4EAF',
      iconType: 'cloud',
      cardBackground: 'rgba(107, 78, 175, 0.08)',
      cardBorder: 'rgba(107, 78, 175, 0.2)',
    },
    'ready': {
      text: 'Ready',
      color: '#047857',
      iconType: 'thunder',
      cardBackground: 'rgba(27, 136, 68, 0.08)',
      cardBorder: 'rgba(27, 136, 68, 0.2)',
    },
    'in_progress': {
      text: 'Live',
      color: '#1B8844',
      iconType: 'play',
      cardBackground: 'rgba(27, 136, 68, 0.08)',
      cardBorder: 'rgba(27, 136, 68, 0.2)',
    },
    'completed': {
      text: 'Completed',
      color: '#15803D',
      iconType: 'check',
      cardBackground: 'rgba(21, 128, 61, 0.08)',
      cardBorder: 'rgba(21, 128, 61, 0.2)',
    },
    'paused': {
      text: 'Paused',
      color: '#B45309',
      iconType: 'pause',
      cardBackground: 'rgba(180, 83, 9, 0.08)',
      cardBorder: 'rgba(180, 83, 9, 0.2)',
    },
    'closed': {
      text: 'Closed',
      color: '#991B1B',
      iconType: 'check',
      cardBackground: 'rgba(153, 27, 27, 0.08)',
      cardBorder: 'rgba(153, 27, 27, 0.2)',
    },
  };
  return statusConfigs[status] || {
    text: status,
    color: '#83899F',
    iconType: 'thunder',
    cardBackground: '#F5F5F7',
    cardBorder: '#E5E5E5',
  };
};

// Get status icon for Study Detail page
export const getStudyDetailStatusIcon = (status) => {
  const config = getStudyDetailStatusConfig(status);
  return getStudyStatusIcon(config.iconType, config.color);
};

// ============================================
// CALL STATUS TAG
// Used for: CampaignCallsSection.js, TranscriptViewer.js
// ============================================

const callStatusStyles = {
  completed: { background: 'rgba(72, 181, 105, 0.1)', color: '#48B569' },
  pending: { background: 'rgba(229, 170, 17, 0.1)', color: '#E5AA11' },
  processing: { background: 'rgba(66, 171, 222, 0.1)', color: '#42abde' },
  failed: { background: 'rgba(220, 38, 38, 0.1)', color: '#DC2626' },
  no_transcript: { background: 'rgba(131, 137, 159, 0.1)', color: '#83899F' },
};

export const getCallStatusDisplay = (status) => {
  const statusMap = {
    completed: { text: 'Completed', className: 'completed' },
    pending: { text: 'Pending', className: 'pending' },
    in_progress: { text: 'In Progress', className: 'processing' },
    failed: { text: 'Failed', className: 'failed' },
    no_answer: { text: 'No Answer', className: 'no_transcript' },
    incomplete: { text: 'Incomplete', className: 'pending' },
  };
  return statusMap[status] || { text: status || 'Unknown', className: 'no_transcript' };
};

export const CallStatusTag = ({ status }) => {
  const display = getCallStatusDisplay(status);
  const styles = callStatusStyles[display.className] || callStatusStyles.no_transcript;
  return (
    <Tag
      style={{
        borderRadius: '6px',
        padding: '4px 10px',
        margin: 0,
        height: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.4,
        gap: '4px',
        border: 'none',
        backgroundColor: styles.background,
        color: styles.color,
      }}
    >
      {display.text}
    </Tag>
  );
};

// ============================================
// TRANSCRIPT STATUS TAG
// Used for: CampaignCallsSection.js
// ============================================

const transcriptStatusStyles = {
  completed: { background: 'rgba(72, 181, 105, 0.1)', color: '#48B569' },
  pending: { background: 'rgba(229, 170, 17, 0.1)', color: '#E5AA11' },
  processing: { background: 'rgba(66, 171, 222, 0.1)', color: '#42abde' },
  failed: { background: 'rgba(220, 38, 38, 0.1)', color: '#DC2626' },
  not_available: { background: 'rgba(131, 137, 159, 0.1)', color: '#83899F' },
};

export const getTranscriptStatusDisplay = (status) => {
  const statusMap = {
    uploaded: { text: 'Available', className: 'completed' },
    completed: { text: 'Available', className: 'completed' },
    pending: { text: 'Pending', className: 'pending' },
    processing: { text: 'Processing', className: 'processing' },
    failed: { text: 'Failed', className: 'failed' },
    skipped: { text: 'Skipped', className: 'not_available' },
    not_available: { text: 'Not Available', className: 'not_available' },
  };
  return statusMap[status] || { text: status || 'Unknown', className: 'not_available' };
};

export const TranscriptStatusTag = ({ status }) => {
  const display = getTranscriptStatusDisplay(status);
  const styles = transcriptStatusStyles[display.className] || transcriptStatusStyles.not_available;
  return (
    <Tag
      style={{
        borderRadius: '6px',
        padding: '4px 10px',
        margin: 0,
        height: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.4,
        gap: '4px',
        border: 'none',
        backgroundColor: styles.background,
        color: styles.color,
      }}
    >
      {display.text}
    </Tag>
  );
};

// ============================================
// AUDIO STATUS TAG
// Used for: CampaignCallsSection.js
// ============================================

const audioStatusStyles = {
  uploaded: { background: 'rgba(72, 181, 105, 0.1)', color: '#48B569' },
  pending: { background: 'rgba(229, 170, 17, 0.1)', color: '#E5AA11' },
  processing: { background: 'rgba(66, 171, 222, 0.1)', color: '#42abde' },
  failed: { background: 'rgba(220, 38, 38, 0.1)', color: '#DC2626' },
  not_available: { background: 'rgba(131, 137, 159, 0.1)', color: '#83899F' },
};

export const getAudioStatusDisplay = (status) => {
  const statusMap = {
    uploaded: { text: 'Available', className: 'uploaded' },
    pending: { text: 'Pending', className: 'pending' },
    processing: { text: 'Processing', className: 'processing' },
    failed: { text: 'Failed', className: 'failed' },
    not_available: { text: 'Not Available', className: 'not_available' },
  };
  return statusMap[status] || { text: status || 'Unknown', className: 'not_available' };
};

export const AudioStatusTag = ({ status }) => {
  const display = getAudioStatusDisplay(status);
  const styles = audioStatusStyles[display.className] || audioStatusStyles.not_available;
  return (
    <Tag
      style={{
        borderRadius: '6px',
        padding: '4px 10px',
        margin: 0,
        height: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.4,
        gap: '4px',
        border: 'none',
        backgroundColor: styles.background,
        color: styles.color,
      }}
    >
      {display.text}
    </Tag>
  );
};

// ============================================
// PROJECT STATUS TAG
// Used for: Project.js (Project listing page)
// ============================================

export const getProjectStatusConfig = (status) => {
  const statusConfigs = {
    draft: { className: 'blueDarkBorder', icon: '/images/on-hold-tag-icon.svg', text: 'Draft' },
    indexing: { className: 'orangeBorder', icon: '/images/in-review-tag-icon.svg', text: 'Indexing' },
    ready: { className: 'skyBlueBorder', icon: '/images/active-tag-icon.svg', text: 'Ready' },
    deleting: { className: 'redBorder', icon: '/images/delete-icon.svg', text: 'Deleting...' },
  };
  return statusConfigs[status] || statusConfigs.draft;
};

export const ProjectStatusTag = ({ status }) => {
  const config = getProjectStatusConfig(status);
  return (
    <ProjectActiveTag className={config.className}>
      <Space size={6}>
        {status === 'deleting' ? (
          <LoadingOutlined spin />
        ) : (
          <img src={config.icon} alt={config.text} />
        )}
        {config.text}
      </Space>
    </ProjectActiveTag>
  );
};

// ============================================
// QUALITY TAG
// Used for: CampaignCallsSection.js - Good/Bad quality display
// ============================================

const qualityStyles = {
  good: { background: 'rgba(72, 181, 105, 0.1)', color: '#48B569' },
  bad: { background: 'rgba(61, 41, 105, 0.1)', color: '#3d2969' },
};

export const QualityTag = ({ quality }) => {
  const isGood = quality === 'good';
  const styles = isGood ? qualityStyles.good : qualityStyles.bad;
  return (
    <Tag
      style={{
        borderRadius: '6px',
        padding: '4px 10px',
        margin: 0,
        height: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.4,
        border: 'none',
        backgroundColor: styles.background,
        color: styles.color,
      }}
    >
      {isGood ? 'Good' : 'Bad'}
    </Tag>
  );
};

export default StatusTag;
